import { useCallback, useMemo, useState } from "react";
import { Typography } from "antd";
import { classNames } from "utils/helpers";
import {
  FullscreenOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { VEHICLE_COLOR_TYPE } from "../../../../utils/mapped-data";
import "styles/components/tab-content/vehicles-content/image-catalog.scss";

const { Text } = Typography;

export default function ImageCatalog() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const {
    vehicles: { selectedVehicle },
  } = useSelector<AppState, AppState>((state) => state);

  // Dynamically generate catalog based on vehicle color
  const catalog = useMemo(() => {
    switch (selectedVehicle?.color) {
      case 2:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/SKY_BLUE/SkyblueZUM2000.jpeg",
            name: "Sky Blue ZUM 2000",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/blue.jpg",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
      case 3:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/BEIGE/WhatsApp%20Image%202025-03-05%20at%201.40.54%20AM.jpeg",
            name: "Beige ZUM 2000",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/beige.jpg",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
      case 4:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/CHROME_SILVER/silverchrome.jpeg",
            name: "Chrome Silver ZUM 2000",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/chroom.jpg",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
      case 5:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/BLACK/WhatsApp%20Image%202025-03-04%20at%2011.21.14%20PM.jpeg",
            name: "Black ZUM 2000",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/black.png",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
      case 6:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/GUN_METAL_GRAY/gumetal%20grey.jpeg",
            name: "Gun Metal ZUM 2000",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/silver.jpg",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
      default:
        return [
          {
            imageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/image11.png",
            name: "Default Image",
            rickshawImageUrl:
              "https://all-flux-documents.nyc3.digitaloceanspaces.com/zyp_pics/rickshaw/red.png",
            rickshawName: "Sazgar Rickshaw",
          },
        ];
    }
  }, [selectedVehicle?.color]);

  const onChange = useCallback(
    (index) => {
      setSelectedIndex(index);
    },
    []
  );

  const onNext = useCallback(() => {
    setSelectedIndex((index) => {
      return index === catalog.length - 1 ? index : index + 1;
    });
  }, [catalog.length]);

  const onBack = useCallback(() => {
    setSelectedIndex((index) => {
      return index === 0 ? index : index - 1;
    });
  }, []);

  // Touch event handlers for swipe navigation on mobile
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      onNext();
    }
    if (isRightSwipe) {
      onBack();
    }
  };

  const currentImage = selectedVehicle?.model_type == 2 
    ? catalog[selectedIndex]?.rickshawImageUrl 
    : catalog[selectedIndex]?.imageUrl;

  return (
    <div className="image-container">
      <div
        className="vehicle-image"
        style={
          {
            "--vehicle-catalog-image": `url(${currentImage})`,
          } as React.CSSProperties
        }
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/*<Text className="full-screen">
          Photo Taken on Jan 01, 2020 by Harris Shams <FullscreenOutlined />
        </Text>*/}
        <div className="navigators">
          <div
            role="button"
            className={classNames({
              "nav-btn": true,
              "nav-back": true,
              "--disabled": selectedIndex === 0,
            })}
            onClick={onBack}
            aria-label="Previous image"
          >
            <LeftOutlined />
          </div>
          <div
            role="button"
            className={classNames({
              "nav-btn": true,
              "nav-next": true,
              "--disabled": selectedIndex === catalog.length - 1,
            })}
            onClick={onNext}
            aria-label="Next image"
          >
            <RightOutlined />
          </div>
        </div>
      </div>
      <div className="vehicle-image-catalog">
        {catalog.map((image, index) => {
          const classes = classNames({
            thumbnail: true,
            "--selected": index === selectedIndex,
          });
          return (
            <img
              key={image.imageUrl}
              className={classes}
              src={
                selectedVehicle?.model_type == 2
                  ? image.rickshawImageUrl
                  : image.imageUrl
              }
              alt={`${image.name} - Thumbnail ${index + 1}`}
              onClick={() => onChange(index)}
            />
          );
        })}
      </div>
    </div>
  );
}